import axios from "axios";
import { Marker, Popup, useMap } from "react-leaflet";
import L from 'leaflet';

import cctvSuccessIcon from 'pictures/cctv_success.png';
import cctvFailIcon from 'pictures/cctv_fail.png';
import panelIcon from 'pictures/panel.png';
import sosIcon from 'pictures/sos.png';
import stationIcon from 'pictures/station.png';
import weightIcon from 'pictures/weight.png';
import computerIcon from 'pictures/computer.png';
import useConfirm from "hooks/useConfirm";
import { useAuth } from "services/AuthContext";
import { useNotificationContext } from "services/NotificationContext";
import { useEffect } from "react";
import { IAsset } from "models/Assets";
import MarkerPMV from "./Markers/MarkerPMV";
import { FormState } from "pages/PMV/Aspects";
import MarkerCCTV from "./Markers/MarkerCCTV";
import MarkerSOS from "./Markers/MarkerSOS";
import { IPMV } from "pages/PMV/PMVHooks";
import { DEFAULT_PANEL } from "pages/PMV/DefaultPanels";
import MarkerAPD from "./Markers/MarkerAPD";
import MarkerETD from "./Markers/MarkerETD";
import MarkerEM from "./Markers/MarkerEM";
import ButtonIcon from "components/ButtonComp";
import { faDownload, faEdit, faPhoneAlt, faServer, faTemperatureHalf, faVideoCamera } from "@fortawesome/free-solid-svg-icons";

const MapViewHooks = () => {
    const { REACT_APP_VICTUM_ONVIF: VICTUM_ONVIF_URL } = process.env;
    const { showPromiseConfirm } = useConfirm()
    const { logout } = useAuth();
    const { show } = useNotificationContext()
    // Definir el icono personalizado
    const customIcon = (iconUrl: string): L.Icon => {
        return L.icon({
            iconUrl: iconUrl,
            iconSize: [50, 50], // Ajusta el tamaño del icono según tus necesidades
            iconAnchor: [16, 32], // Ancla en el centro inferior del icono
            popupAnchor: [0, -32], // Ajusta la posición del popup
        });
    };

    // Método para descargar el cliente ONVIF
    const descargarClienteONVIF = () => {
        if (VICTUM_ONVIF_URL) {
            window.open(VICTUM_ONVIF_URL, '_blank');
        } else {
            show('error', 'URL no disponible', 'No se encontró la URL de descarga del cliente ONVIF.');
        }
    };
    // Proceso de flujo de video
    const openStream = async (asset: IAsset): Promise<void> => {
        // console.log("🚀 ~ openStream ~ asset:", asset)
        try {
            // const response = await axios.post(`${process.env.REACT_APP_LOCAL_SERVER}/api/ffmpeg/start`, {
            //     id: asset.id,
            //     rtspUrl: asset.url,
            //     outputPath: "./hls",
            // });
            // console.log("🚀 ~ openStream ~ response:", response);
            const { user, pass, xaddr, rtspUrl } = asset;
            const urlVLC = rtspUrl;
            const urlCCTV = new URL(xaddr || '');
            const hostname = urlCCTV.hostname;
            const response = await axios.get(`http://localhost:8083`, { params: { url: hostname, urlVLC, user, pass } });
            // console.log("🚀 ~ openStream ~ response:", response)
            if (response.status === 200) {
                if (response.data?.status !== 'success') {
                    show('warning', 'Ha ocurrido un error', <>
                        Verifique el agente de Victum ITS Onvif Viewer & PTZ se esté ejecutando en su equipo.
                        <br />
                        <ButtonIcon icon={faDownload} name="Descargar cliente ONVIF" onClick={descargarClienteONVIF} variant="link"/>
                    </>);
                }
            } else {
                show('error', 'Ha ocurrido un error', <>
                    Verifique el agente de Victum ITS Onvif Viewer & PTZ se esté ejecutando en su equipo.
                    <br />
                    <ButtonIcon icon={faDownload} name="Descargar cliente ONVIF" onClick={descargarClienteONVIF} variant="link"/>
                </>);
            }
        } catch (error) {
            console.error(`Error al abrir proceso:`, error);
            show('error', 'Ha ocurrido un error', <>
                Verifique el agente de Victum ITS Onvif Viewer & PTZ se esté ejecutando en su equipo.
                <br />
                <ButtonIcon icon={faDownload} name="Descargar cliente ONVIF" onClick={descargarClienteONVIF} variant="link"/>
            </>);
        }
    };

    const abrirVentanaNueva = async (asset: IAsset, type: string): Promise<void> => {
        if (type === "cctv") {
            await openStream(asset);
            return;
        }
        const url = `${window.location.origin}/${type}/${asset.id}`;
        const opciones = 'width=800,height=500,top=100,left=100';
        window.open(url, '_blank', opciones);
    };

    const abrirEstacionMeteorologica = async (asset: IAsset): Promise<void> => {
        const url = `${window.location.origin}/weather-station/${asset.id}`;
        const opciones = 'width=800,height=700,top=100,left=100';
        window.open(url, '_blank', opciones);
    };

    const abrirETD = async (asset: IAsset): Promise<void> => {
        const url = `${window.location.origin}/etd/${asset.id}`;
        const opciones = 'width=800,height=700,top=100,left=100';
        window.open(url, '_blank', opciones);
    };

    const openListPMV = async (): Promise<void> => {
        const url = `${window.location.origin}/pmv-all`;
        const opciones = 'width=800,height=500,top=100,left=100';
        window.open(url, '_blank', opciones);
    };

    const openMainSOS = async (): Promise<void> => {
        const url = `${window.location.origin}/sos`;
        const opciones = 'width=800,height=500,top=100,left=100';
        window.open(url, '_blank', opciones);
    };

    const openMenuItem = async (option: string): Promise<void> => {
        const url = `${window.location.origin}/${option}`;
        const opciones = 'width=800,height=500,top=100,left=100';
        const windowName = `window_${option || 'default'}`;
        window.open(url, windowName, opciones);
    };

    const hasTwoHyphens = (str: string): boolean => str.split('-').length === 3;

    // Componente para enfocar en una ubicación específica
    const FocusOnMarker = ({ position }: { position: [number, number] }): null => {
        const map = useMap();
        useEffect(() => {
            map.setView(position, map.getZoom());
        }, [map, position]);
        // map.setView(position, 16); // Ajusta el nivel de zoom aquí
        return null;
    };

    const getIcon = (asset: IAsset): L.Icon | undefined => {
        switch (asset.type_id) {
            case 20: return customIcon(panelIcon);
            case 21: return asset.url ? customIcon(cctvSuccessIcon) : customIcon(cctvFailIcon);
            case 22: return customIcon(sosIcon);
            case 23: return customIcon(computerIcon);
            case 24: return customIcon(stationIcon);
            case 25: return customIcon(weightIcon);
            default: return undefined;
        }
    };

    const defaultComp = (asset: IAsset): JSX.Element => (
        <div>
            <h4>{asset.description}</h4>
            <ButtonIcon
                icon={faEdit}
                variant="outline-primary"
                name="Abrir elemento"
                onClick={() => show('warning', 'Elemento fuera de línea', 'El elemento está desconectado')}
            />
        </div>
    );

    const getContainer = (asset: IAsset): JSX.Element => {
        switch (asset.type_id) {
            case 20: return (
                <div>
                    <h4>{asset.description}</h4>
                    <ButtonIcon
                        icon={faEdit}
                        variant="outline-primary"
                        name="Editar panel"
                        onClick={() => abrirVentanaNueva(asset, 'pmv')}
                    />
                </div>
            );
            case 21: return (
                <div>
                    <h4>{asset.description}</h4>
                    <ButtonIcon
                        icon={faVideoCamera}
                        variant="outline-primary"
                        name="Visualizar cámara"
                        onClick={
                            () => asset.is_online ?
                                abrirVentanaNueva(asset, 'cctv') :
                                show('warning', 'Cámara fuera de línea', 'No se puede acceder al video de esta cámara')
                        }
                    />
                </div>
            );
            case 22: return (
                <div>
                    <h4>{asset.description}</h4>
                    {/* <ButtonIcon
                        icon={faPhoneAlt}
                        variant="outline-primary"
                        name="Visualizar SOS"
                        onClick={() => abrirVentanaNueva(asset, 'sos')}
                    /> */}
                </div>
            );
            case 23: return (
                <div>
                    <h4>{asset.description}</h4>
                    <ButtonIcon
                        icon={faServer}
                        variant="outline-primary"
                        name="Ver ETD"
                        onClick={() => abrirETD(asset)}
                    />
                </div>
            );
            case 24: return (
                <div>
                    <h4>{asset.description}</h4>
                    <ButtonIcon
                        icon={faTemperatureHalf}
                        variant="outline-primary"
                        name="Ver estación meteorológica"
                        onClick={() => abrirEstacionMeteorologica(asset)}
                    />
                </div>
            );
            default: return defaultComp(asset);
        }
    };

    const signOut = () => {
        showPromiseConfirm({
            title: 'Cerrar sesión',
            content: <>
                <p>Está seguro que desea cerrar la sesión</p>
            </>,
            okText: 'Cerrar sesión',
            actionCallback: async () => {
                show("info", "Sesión cerrada", "Se han removido los permisos para Victum ITS");
                logout();
            }
        });
    }

    const getMarker = (
        asset: IAsset,
        formStates: IPMV[],
        setFocusedPosition: (pos: [number, number] | null) => void
    ) => {
        const { latitude, longitude } = asset;
        const pos: [number, number] | null = latitude && longitude ? [latitude, longitude] : null;
        switch (asset.type_id) {
            case 20:
                const currentFormState: FormState[] = formStates.find((formState) => formState.id === asset.id)?.formState || DEFAULT_PANEL;
                return <MarkerPMV
                    key={asset.id}
                    formState={currentFormState}
                    asset={asset}
                    eventHandlers={{
                        click: () => setFocusedPosition(pos)
                    }} // Al hacer click en un marcador se enfoca en él
                >
                    <Popup>{getContainer(asset)}</Popup>
                </MarkerPMV>
            case 21:
                return <MarkerCCTV
                    key={asset.id}
                    asset={asset}
                    eventHandlers={{
                        click: () => setFocusedPosition(pos)
                    }}
                >
                    <Popup>{getContainer(asset)}</Popup>
                </MarkerCCTV >
            case 22:
                return <MarkerSOS
                    key={asset.id}
                    asset={asset}
                    eventHandlers={{
                        click: () => setFocusedPosition(pos)
                    }}
                >
                    <Popup>{getContainer(asset)}</Popup>
                </MarkerSOS>
            case 23:
                return <MarkerETD
                    key={asset.id}
                    asset={asset}
                    eventHandlers={{
                        click: () => setFocusedPosition(pos)
                    }}
                >
                    <Popup>{getContainer(asset)}</Popup>
                </MarkerETD>
            case 24:
                return <MarkerEM
                    key={asset.id}
                    asset={asset}
                    eventHandlers={{
                        click: () => setFocusedPosition(pos)
                    }}
                >
                    <Popup>{getContainer(asset)}</Popup>
                </MarkerEM>
            case 25:
                return <MarkerAPD
                    key={asset.id}
                    asset={asset}
                    eventHandlers={{
                        click: () => setFocusedPosition(pos)
                    }}
                >
                    <Popup>{getContainer(asset)}</Popup>
                </MarkerAPD>

            default:
                if (pos) {
                    return <Marker
                        key={asset.id}
                        draggable={true}
                        position={pos}
                        icon={getIcon(asset)}
                        eventHandlers={{
                            click: () => setFocusedPosition(pos)
                        }}

                    >
                        <Popup>{getContainer(asset)}</Popup>
                    </Marker>
                }
                return null;
        }
    }
    return {
        customIcon,
        abrirVentanaNueva,
        openListPMV,
        openMenuItem,
        hasTwoHyphens,
        FocusOnMarker,
        getIcon,
        defaultComp,
        getContainer,
        signOut,
        getMarker,
        openMainSOS
    }
}

export default MapViewHooks